import React, { useState } from "react";

import {
	IonButton,
	IonButtons,
	IonContent,
	IonHeader,
	IonIcon,
	IonInput,
	IonItem,
	IonModal,
	IonSpinner,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";

import { close } from "assets/icons";
import { Auth } from "components/common/Auth/Auth";
import { Button } from "components/common/Button";
import Asset from "models/Asset";
import Project from "models/Project";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { postAsset } from "api/assets";
import syncFunction from "utils/sync";
import { performBackgroundSyncThunk } from "store/slices/remote/thunks";
import { useAppDispatch } from "store";
import useToken from "components/common/Auth/hooks/useToken";

interface IProps {
	isOpen: boolean;
	projectRef: string;
	assetId?: string;
	onClose: () => void;
}

const rules = { required: true };
const initialValues = { name: "" };

export const CreateAssetModal: React.FC<IProps> = (props: IProps) => {
	const { isOpen, onClose } = props;
	const { t, i18n } = useTranslation();
	const dispatch = useAppDispatch();
	const token = useToken();
	const { projectRef } = useParams<{ projectRef: string }>();
	const [showErrorToast, setShowErrorToast] = useState(false);

	const { handleSubmit, formState, control, reset } = useForm({
		defaultValues: initialValues,
		reValidateMode: "onBlur",
	});

	const createNewAsset = async (name = "New Asset"): Promise<string> => {
		const project = await Project.get(projectRef);
		const assetResponse = await postAsset({
			name: name,
			project_ref: project.ref,
		});
		await Asset.set(new Asset(assetResponse));
		return assetResponse.id;
	};

	const onSubmit = (data: { name: string }) => {
		return createNewAsset(data.name)
			.then(async (newAssetId) => {
				if (!token) {
					throw new Error("Token could not be retrieved to perform sync after asset creation");
				}
				await syncFunction(token, [], [], [], projectRef, newAssetId);
				await dispatch(performBackgroundSyncThunk({ projectRef }));
				reset();
				onClose();
			})
			.catch((err) => {
				console.error(err);
				setShowErrorToast(true);
			});
	};

	return (
		<IonModal
			isOpen={isOpen}
			canDismiss={true}
			backdropDismiss={true}
			onDidDismiss={() => {
				onClose();
			}}
		>
			<Auth>
				<IonHeader mode="ios">
					<IonToolbar style={{ alignItems: "center", "--border-width": 0 }}>
						<IonTitle>{i18n.format(t("create_asset_header"), "capitalize")}</IonTitle>
						<IonButtons slot="end">
							<IonButton onClick={onClose}>
								<IonIcon src={close} size="small" style={{ strokeWidth: 1 }} />
							</IonButton>
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent forceOverscroll={false}>
					<form
						id="create-asset-modal-form"
						onSubmit={handleSubmit(onSubmit, (errors) => console.warn(errors))}
						style={{
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							padding: "0rem 1.5rem",
							paddingTop: "1rem",
						}}
					>
						<div>
							<Controller
								name="name"
								control={control}
								render={({ field, formState }) => {
									const label = [
										i18n.format(t("create_asset_name"), "capitalize"),
										...(formState.errors.name?.type === "required"
											? [i18n.format(t("create_asset_required_warning"), "capitalize")]
											: []),
									].join(" ");
									return (
										<IonItem>
											<IonInput
												{...field}
												label={label}
												labelPlacement="stacked"
												onIonBlur={field.onBlur}
												onIonInput={field.onChange}
											/>
										</IonItem>
									);
								}}
								rules={rules}
							></Controller>
						</div>
						<div style={{ display: "flex", flexDirection: "column", marginInline: "1rem", marginBottom: "2rem" }}>
							<Button
								variant="filled"
								type="submit"
								disabled={formState.isSubmitting || Object.keys(formState.errors).length > 0}
							>
								{formState.isSubmitting ? (
									<IonSpinner name="crescent" />
								) : (
									i18n.format(t("create_asset_submit_button"), "capitalizeEveryWord")
								)}
							</Button>
						</div>
					</form>
					<IonToast
						isOpen={showErrorToast}
						onDidDismiss={() => setShowErrorToast(false)}
						message={i18n.format(t("create_asset_error"), "capitalize")}
						duration={1500}
					/>
				</IonContent>
			</Auth>
		</IonModal>
	);
};

const propsAreEqual = (p1: IProps, p2: IProps) =>
	p1.assetId === p2.assetId && p1.isOpen === p2.isOpen && p1.projectRef === p2.projectRef;
export default React.memo(CreateAssetModal, propsAreEqual);
